/**
 * Reseller bookings — list + detail. Reads /portal/reseller/bookings
 * (Pass D follow-up). Status pills mirror the customer portal so the
 * reseller and the customer see the same trip in the same words.
 */
import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { newIdempotencyKey } from '../../utils/idempotency'
import ResellerShell from './ResellerShell'
import CommunicationHistory from '../../components/CommunicationHistory'
import NetRateConfirmForm from '../../components/NetRateConfirmForm'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import PartialRefundModal from '../../components/PartialRefundModal'
import RefundInProgressPanel from '../../components/RefundInProgressPanel'
import RefundApprovalPanel from '../../components/RefundApprovalPanel'

// Statuses safe to hard-delete (abandoned / never paid). Mirrors the backend;
// the server additionally blocks anything with an authorized/captured payment.
const DELETABLE_BOOKING_STATUSES = ['building', 'pending_payment', 'awaiting_payment']

const STATUS_BUCKETS = {
  pending:     ['awaiting_payment','payments_complete','booking_in_progress','pending_customer_action','pending_ops_action','pending_payment','building'],
  in_progress: ['confirmed','partially_confirmed'],
  completed:   ['completed','refunded','cancelled_by_customer','cancelled_by_provider','cancelled_failed_payment','cancelled','failed','partial_refund','no_show'],
}
function bucketFor(status) {
  for (const [k, vs] of Object.entries(STATUS_BUCKETS)) if (vs.includes(status)) return k
  return 'pending'
}

// Origin of a booking. All three write to reseller_bookings; the discriminators
// are Quote_ID (a materialized CTQ trip links back to its quote) and Package_ID
// (S&P embed bookings carry a package; partner-API bookings carry neither).
function bookingType(b) {
  if (b.Quote_ID) return 'ctq'
  if (b.Package_ID) return 'sp'
  return 'partner'
}
function bookingTypeLabel(t, type) {
  return {
    sp:      t('sp_type_sp', 'S&P'),
    ctq:     t('sp_type_ctq', 'CTQ'),
    partner: t('sp_type_partner', 'Partner'),
  }[type] || type
}
function TypePill({ type, t }) {
  const palette = {
    sp:      { bg: '#e0f2fe', fg: '#075985' },
    ctq:     { bg: '#ede9fe', fg: '#6d28d9' },
    partner: { bg: '#f1f5f9', fg: '#475569' },
  }[type] || { bg: '#f1f5f9', fg: '#475569' }
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, background: palette.bg, color: palette.fg,
      letterSpacing: 0.3,
    }}>{bookingTypeLabel(t, type)}</span>
  )
}

// Tab → predicate. 'booked_paid' (confirmed) and 'building' are single-status
// subset filters — they intentionally overlap the coarse buckets (confirmed also
// lives in 'in_progress', building in 'pending'); everything else matches the
// coarse bucket.
function matchesFilter(status, filter) {
  if (filter === 'all') return true
  if (filter === 'booked_paid') return status === 'confirmed'
  if (filter === 'building') return status === 'building'
  return bucketFor(status) === filter
}

// Filter-tab labels. The two headline filters ('Booked and Paid', 'Building')
// are title-cased on purpose; the coarse buckets stay lowercase as before.
function bucketLabel(t, bucket) {
  const map = {
    booked_paid: t('sp_filter_booked_paid', 'Booked and Paid'),
    all:         t('sp_filter_all_lc', 'all'),
    pending:     t('sp_filter_pending_lc', 'pending'),
    in_progress: t('sp_filter_in_progress_lc', 'in progress'),
    completed:   t('sp_filter_completed_lc', 'completed'),
    building:    t('sp_filter_building', 'Building'),
  }
  return map[bucket] || bucket.replace(/_/g, ' ')
}

// Booking / component / payment status labels. Falls back to the prior
// underscores→spaces formatting for any value without a dedicated key.
function statusLabel(t, status) {
  const map = {
    awaiting_payment:          t('sp_bk_status_awaiting_payment', 'awaiting payment'),
    payments_complete:         t('sp_bk_status_payments_complete', 'payments complete'),
    booking_in_progress:       t('sp_bk_status_booking_in_progress', 'booking in progress'),
    pending_customer_action:   t('sp_bk_status_pending_customer_action', 'pending customer action'),
    pending_ops_action:        t('sp_bk_status_pending_ops_action', 'pending ops action'),
    pending_payment:           t('sp_bk_status_pending_payment', 'pending payment'),
    building:                  t('sp_bk_status_building', 'building'),
    confirmed:                 t('sp_bk_status_confirmed', 'confirmed'),
    partially_confirmed:       t('sp_bk_status_partially_confirmed', 'partially confirmed'),
    completed:                 t('sp_bk_status_completed', 'completed'),
    refunded:                  t('sp_bk_status_refunded', 'refunded'),
    partial_refund:            t('sp_bk_status_partial_refund', 'partial refund'),
    cancelled_by_customer:     t('sp_bk_status_cancelled_by_customer', 'cancelled by customer'),
    cancelled_by_provider:     t('sp_bk_status_cancelled_by_provider', 'cancelled by provider'),
    cancelled_failed_payment:  t('sp_bk_status_cancelled_failed_payment', 'cancelled failed payment'),
    cancelled:                 t('sp_bk_status_cancelled', 'cancelled'),
    failed:                    t('sp_bk_status_failed', 'failed'),
    failed_pending_action:     t('sp_bk_status_failed_pending_action', 'failed pending action'),
    deferred:                  t('sp_bk_status_deferred', 'scheduled'),
    awaiting_confirmation:     t('sp_bk_status_awaiting_confirmation', 'awaiting confirmation'),
    no_show:                   t('sp_bk_status_no_show', 'no show'),
    pending:                   t('sp_bk_status_pending', 'pending'),
    authorized:                t('sp_bk_status_authorized', 'authorized'),
    captured:                  t('sp_bk_status_captured', 'captured'),
    voided:                    t('sp_bk_status_voided', 'voided'),
    declined:                  t('sp_bk_status_declined', 'declined'),
  }
  return map[status] || String(status || '').replace(/_/g, ' ')
}

export default function Bookings() {
  const { t } = useLanguage()
  return (
    <ResellerShell title={t('sp_nav_bookings', 'Bookings')}>
      <BookingsList />
    </ResellerShell>
  )
}

function BookingsList() {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [filter, setFilter] = useState('booked_paid')
  const [busy, setBusy] = useState(false)
  const [q, setQ] = useState('')

  function reload() {
    tournamentApi.get('/portal/reseller/bookings')
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(reload, [])

  // Only abandoned/never-paid bookings are deletable; the server also blocks
  // anything with an authorized/captured payment.
  async function deleteOne(id) {
    if (!window.confirm(t('sp_delete_booking_confirm', 'Delete booking #{id}? This permanently removes the abandoned booking.', { id }))) return
    setBusy(true)
    try { await tournamentApi.delete(`/portal/reseller/bookings/${id}`); reload() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function deleteAbandoned(n) {
    if (!window.confirm(t('sp_delete_abandoned_confirm', 'Delete all {n} abandoned bookings (building + unpaid)? Anything with a payment is left untouched.', { n }))) return
    setBusy(true)
    try {
      const { data: r } = await tournamentApi.post('/portal/reseller/bookings/delete-abandoned')
      alert(t('sp_deleted_n_bookings', 'Deleted {n} bookings.', { n: r.deleted }))
      reload()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  if (err) return <div style={errorBox}>{err}</div>
  if (!data) return <div style={{ color: '#64748b' }}>{t('sp_loading_bookings', 'Loading bookings…')}</div>

  const counts = { all: data.total, booked_paid: 0, building: 0 }
  for (const b of data.bookings) {
    const k = bucketFor(b.Status)
    counts[k] = (counts[k] || 0) + 1
    if (b.Status === 'confirmed') counts.booked_paid += 1
    if (b.Status === 'building')  counts.building += 1
  }
  // One-box search across the loaded bookings — booking #, customer name/email/
  // phone, package/grouping, dates, total, and status label. When searching we
  // span ALL bookings (the status tabs are ignored) so it finds everything.
  const needle = q.trim().toLowerCase()
  const matchesSearch = (b) => {
    if (!needle) return true
    const hay = [
      b.ID_NO, `#${b.ID_NO}`, b.GolferName, b.GolferEmail, b.GolferPhone,
      b.PackageName, b.GroupingName, b.CheckInDate, b.CheckOutDate,
      b.Status, statusLabel(t, b.Status), b.TotalCustomerPaid, b.Currency,
      bookingTypeLabel(t, bookingType(b)),
    ].filter(v => v != null && v !== '').join(' ').toLowerCase()
    if (hay.includes(needle)) return true
    // Phone / number match on digits only, so "902555" finds "(902) 555-…".
    const nd = needle.replace(/\D/g, '')
    const phoneDigits = String(b.GolferPhone || '').replace(/\D/g, '')
    return !!nd && phoneDigits.includes(nd)
  }
  const filtered = needle
    ? data.bookings.filter(matchesSearch)
    : data.bookings.filter(b => matchesFilter(b.Status, filter))
  // Server-computed over ALL bookings (the list itself is paged), so this
  // matches exactly what "Delete abandoned" will remove.
  const abandonedCount = data.deletableCount || 0

  return (
    <>
      <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, maxWidth: 720 }}>
        {t('sp_bookings_intro', 'Every booking made on your reseller account, with hotel + tee-time confirmation status. Click any row for the full breakdown including payment and audit history.')}
      </p>

      <div style={{ margin: '18px 0 0', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={t('sp_bookings_search_ph', 'Search bookings — name, email, #, package, status, date…')}
          style={{ flex: '1 1 360px', maxWidth: 480, padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
        />
        {needle && (
          <span style={{ fontSize: 12.5, color: '#64748b' }}>
            {t('sp_bookings_n_results', '{n} result(s)', { n: filtered.length })} ·{' '}
            <button type="button" onClick={() => setQ('')} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12.5, padding: 0 }}>
              {t('sp_clear', 'clear')}
            </button>
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, margin: '14px 0 20px', flexWrap: 'wrap', alignItems: 'center', opacity: needle ? 0.45 : 1, pointerEvents: needle ? 'none' : 'auto' }}>
        {['booked_paid','all','pending','in_progress','completed','building'].map(b => (
          <button
            key={b}
            type="button"
            onClick={() => setFilter(b)}
            style={{
              padding: '6px 12px',
              background: filter === b ? '#0f172a' : '#fff',
              color: filter === b ? '#fff' : '#475569',
              border: '1px solid ' + (filter === b ? '#0f172a' : '#e2e8f0'),
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {bucketLabel(t, b)} {counts[b] != null ? `(${counts[b]})` : ''}
          </button>
        ))}
        {abandonedCount > 0 && (
          <button type="button" onClick={() => deleteAbandoned(abandonedCount)} disabled={busy}
            style={{ marginLeft: 'auto', padding: '6px 12px', background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {t('sp_delete_abandoned_btn', 'Delete abandoned ({n})', { n: abandonedCount })}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 32, textAlign: 'center', color: '#64748b' }}>
          {needle
            ? t('sp_no_bookings_for_search', 'No bookings match "{q}".', { q })
            : filter !== 'all'
              ? t('sp_no_bookings_in_filter', 'No bookings in {filter}.', { filter: bucketLabel(t, filter) })
              : t('sp_no_bookings_yet_period', 'No bookings yet.')}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={th}>{t('sp_booking_label', 'Booking')}</th>
                <th style={th}>{t('sp_th_type', 'Type')}</th>
                <th style={th}>{t('sp_th_customer', 'Customer')}</th>
                <th style={th}>{t('sp_ctq_col_booked', 'Date of Booking')}</th>
                <th style={th}>{t('sp_ctq_col_trip_date', 'Date of Trip')}</th>
                <th style={th}>{t('sp_th_components', 'Components')}</th>
                <th style={th}>{t('sp_total', 'Total')}</th>
                <th style={th}>{t('status', 'Status')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => <BookingRow key={b.ID_NO} b={b} onDelete={deleteOne} busy={busy} />)}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function BookingRow({ b, onDelete, busy }) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const canDelete = DELETABLE_BOOKING_STATUSES.includes(b.Status)
  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
        onClick={() => navigate(`/portal/reseller/bookings/${b.ID_NO}`)}>
      <td style={td}>
        <div style={{ fontWeight: 600 }}>#{b.ID_NO}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{b.PackageName} · {b.GroupingName}</div>
      </td>
      <td style={td}><TypePill type={bookingType(b)} t={t} /></td>
      <td style={td}>
        <div>{b.GolferName}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{b.GolferEmail}</div>
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(b.DOE)}</td>
      <td style={td}>
        <div style={{ whiteSpace: 'nowrap' }}>{fmtDate(b.CheckInDate)} → {fmtDate(b.CheckOutDate)}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{t('sp_golfers_rooms_short', '{golfers}g · {rooms}r', { golfers: b.NumGolfers, rooms: b.NumRooms })}</div>
      </td>
      <td style={td}>
        {t('sp_components_confirmed', '{confirmed} / {total} confirmed', { confirmed: b.ConfirmedCount || 0, total: b.ComponentCount || 0 })}
        {b.PendingActionCount > 0 && (
          <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
            {t('sp_components_need_attention', '⚠ {count} need attention', { count: b.PendingActionCount })}
          </div>
        )}
        {b.AwaitingConfirmationCount > 0 && (
          <div style={{ fontSize: 11, color: '#6d28d9', fontWeight: 600 }}>
            {t('sp_components_to_confirm', '📋 {count} item(s) to confirm', { count: b.AwaitingConfirmationCount })}
          </div>
        )}
      </td>
      <td style={td}>{fmtMoney(b.TotalCustomerPaid || 0, b.Currency)}</td>
      <td style={td}><StatusPill status={b.Status} /></td>
      <td style={{ ...td, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
        {canDelete && (
          <button type="button" disabled={busy} onClick={() => onDelete(b.ID_NO)} title={t('sp_delete', 'Delete')}
            style={{ padding: '4px 9px', background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {t('sp_delete', 'Delete')}
          </button>
        )}
      </td>
    </tr>
  )
}

// Request-only (concierge) round: the reseller confirms the tee time by hand
// with the course, then records the real time (+ optional reference) here. This
// flips the round awaiting_confirmation -> confirmed and emails the customer.
function RequestConfirmForm({ bookingId, roundIndex, requestedTime, onDone, t }) {
  const [teeTime, setTeeTime] = useState(requestedTime || '')
  const [ref, setRef] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const inp = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5 }
  async function submit() {
    if (!teeTime) { setErr(t('sp_ro_need_time', 'Enter the confirmed tee time.')); return }
    setBusy(true); setErr(null)
    try {
      await tournamentApi.post(`/portal/reseller/bookings/${bookingId}/rounds/${roundIndex}/confirm-request`, { teeTime, ref: ref || null })
      await onDone()
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(false) }
  }
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#5b21b6', marginBottom: 6 }}>
        📋 {t('sp_ro_confirm_heading', 'Request-only course — confirm the tee time with the course, then record it here')}
      </div>
      {requestedTime && <div style={{ fontSize: 11.5, color: '#7c6ba8', marginBottom: 6 }}>{t('sp_ro_customer_requested', 'Customer requested')}: {requestedTime}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ro_confirmed_time', 'Confirmed time')}
          <input type="time" value={teeTime} onChange={e => setTeeTime(e.target.value)} style={inp} /></label>
        <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ro_ref', 'Course ref (optional)')}
          <input type="text" value={ref} onChange={e => setRef(e.target.value)} placeholder={t('sp_ro_ref_ph', 'e.g. Cabot #12345')} style={{ ...inp, width: 150 }} /></label>
        <button onClick={submit} disabled={busy} style={{ padding: '7px 14px', borderRadius: 6, fontWeight: 700, fontSize: 12.5, border: 'none', background: '#6d28d9', color: '#fff', cursor: busy ? 'default' : 'pointer' }}>
          {busy ? t('sp_ro_confirming', 'Confirming…') : t('sp_ro_confirm_btn', 'Confirm tee time')}
        </button>
      </div>
      {err && <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b' }}>{err}</div>}
    </div>
  )
}

function StatusPill({ status }) {
  const { t } = useLanguage()
  const palette = {
    pending:     { bg: '#fef3c7', fg: '#92400e' },
    in_progress: { bg: '#dbeafe', fg: '#1e40af' },
    completed:   { bg: '#d1fae5', fg: '#065f46' },
  }[bucketFor(status)] || { bg: '#e2e8f0', fg: '#475569' }
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px',
      borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: palette.bg, color: palette.fg,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{statusLabel(t, status)}</span>
  )
}

// ─── Detail page (mounted at a separate route) ──────────────────────────

export function BookingDetail() {
  const { t } = useLanguage()
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [releasing, setReleasing] = useState(false)
  const [showPartialRefund, setShowPartialRefund] = useState(false)
  // Stable idempotency key for cancel-and-refund; reused across a retry of the
  // same attempt, cleared on success so a later action gets a fresh one (#237).
  const cancelKeyRef = useRef(null)

  useEffect(() => {
    tournamentApi.get('/portal/reseller/bookings/' + id)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }, [id])

  if (err) return <ResellerShell title={t('sp_booking_label', 'Booking')}><div style={errorBox}>{err}</div></ResellerShell>
  if (!data) return <ResellerShell title={t('sp_booking_label', 'Booking')}><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>

  const { booking: b, components, rounds, payments, hotels = [], hotelDetails = [], integrityIssues = [], refunds = [], refundedTotal = 0, pendingRefund = null, canIssueRefunds = false, canApproveRefunds = false, refundLimit = null } = data

  async function reloadDetail() {
    const r = await tournamentApi.get('/portal/reseller/bookings/' + id)
    setData(r.data)
  }
  async function resolveIssue(checkId) {
    if (!confirm(t('sp_confirm_mark_issue_handled', 'Mark this issue as handled? It will disappear from the dashboard.'))) return
    try {
      await tournamentApi.post(`/portal/reseller/booking-health/${checkId}/resolve`, {})
      await reloadDetail()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    }
  }

  // Reseller "cancel everything + full refund" — cancels the hotel + tee times
  // with the providers and refunds every captured payment (100%).
  const CANCELLABLE = new Set([
    'confirmed', 'partially_confirmed', 'pending_customer_action', 'pending_ops_action',
    'booking_in_progress', 'payments_complete', 'pending_payment', 'awaiting_payment',
  ])
  // 100% of captured is what a full cancel refunds — used to warn up front when
  // it's over the caller's refund limit and will need owner approval (#236).
  const capturedTotal = payments.filter(p => p.Status === 'captured').reduce((s, p) => s + (Number(p.Amount) || 0), 0)
  const cancelNeedsApproval = refundLimit != null && capturedTotal > refundLimit
  async function cancelRefund() {
    const confirmMsg = cancelNeedsApproval
      ? t('pr_needs_approval_warn', "Over your {limit} limit — this refund will need owner approval before it's issued.", { limit: fmtMoney(refundLimit, b.Currency) })
      : t('sp_confirm_cancel_refund', 'Cancel this entire trip and refund the customer in full? This cancels the hotel and any booked tee times with the providers and refunds every captured payment. This cannot be undone.')
    if (!confirm(confirmMsg)) return
    if (!cancelKeyRef.current) cancelKeyRef.current = newIdempotencyKey()
    setBusy(true)
    const doPost = (override) => tournamentApi.post(`/portal/reseller/bookings/${id}/cancel-refund`, { override }, { headers: { 'Idempotency-Key': cancelKeyRef.current } })
    try {
      let r
      try { r = (await doPost(false)).data }
      catch (e) {
        const code = e?.response?.data?.code
        // Completed / past trip (guardrail #239): owner confirms an override; staff blocked.
        if (code === 'NEEDS_OVERRIDE') {
          if (!window.confirm(t('pr_override_confirm', 'This trip is already completed or its dates have passed. Issue the refund anyway?'))) return
          r = (await doPost(true)).data
        } else if (code === 'OWNER_OVERRIDE_REQUIRED') {
          alert(t('pr_override_owner_only', 'This trip is completed or its dates have passed — only the account owner can refund it.')); return
        } else throw e
      }
      cancelKeyRef.current = null
      if (r.awaitingApproval) {
        alert(t('pr_submitted_pending_approval', "Refund set up. It's above your limit, so it was sent to the account owner for approval — the customer won't be refunded until they approve."))
      } else {
        const notes = r.opsNotes || []
        alert(
          (r.refundedAmount > 0
            ? t('sp_cancel_refund_done', 'Trip cancelled. Refunded {amount}.', { amount: fmtMoney(r.refundedAmount, r.currency) })
            : t('sp_cancel_done_no_refund', 'Trip cancelled. No captured payment to refund.'))
          + (notes.length ? '\n\n' + t('sp_cancel_needs_followup', 'Some items need manual follow-up:') + '\n- ' + notes.join('\n- ') : '')
        )
      }
      const res = await tournamentApi.get('/portal/reseller/bookings/' + id)
      setData(res.data)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setBusy(false) }
  }

  // Release still-authorized pre-auths — reverses the temporary hold on the
  // customer's card without moving money. For a booking whose auto-void
  // failed, so a dangling authorization isn't left on the card.
  const hasAuthorizedHold = payments.some(p => p.Status === 'authorized')
  // Partial refund only makes sense once money has actually been captured.
  const hasCapturedPayment = payments.some(p => p.Status === 'captured')
  async function releaseHolds() {
    if (!confirm(t('sp_confirm_release_holds', "Release the authorized hold(s) on this booking? This reverses the pre-authorization on the customer's card. Captured payments are not affected."))) return
    setReleasing(true)
    try {
      const { data: r } = await tournamentApi.post(`/portal/reseller/bookings/${id}/release-holds`, {})
      alert(r.released > 0
        ? t('sp_release_holds_done', 'Released {n} hold(s). The pre-authorization has been reversed on the customer’s card.', { n: r.released })
        : t('sp_release_holds_none', 'No authorized holds to release.'))
      const res = await tournamentApi.get('/portal/reseller/bookings/' + id)
      setData(res.data)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setReleasing(false) }
  }

  // Cancel a still-CONFIRMED hotel with the supplier (RateHawk/Expedia) for a
  // trip that's already cancelled/refunded but whose reservation was never
  // released — so InteGolf isn't on the hook for a hotel nobody is paying for.
  const CANCELLED_TRIP = new Set(['cancelled_by_customer', 'cancelled_by_provider', 'cancelled', 'refunded', 'partial_refund'])
  const hasConfirmedHotel = (components || []).some(c => c.ComponentType === 'hotel' && c.Status === 'confirmed')
  const canReleaseHotel = hasConfirmedHotel && CANCELLED_TRIP.has(b.Status) && canIssueRefunds
  async function releaseHotel() {
    if (!confirm(t('sp_confirm_release_hotel', 'Cancel the hotel reservation with the supplier for this trip? Use this when a trip was already cancelled/refunded but the hotel was never released. This does not move any money.'))) return
    setReleasing(true)
    try {
      const { data: r } = await tournamentApi.post(`/portal/reseller/bookings/${id}/release-hotel`, {})
      const notes = (r.opsNotes || [])
      alert((r.ok ? t('sp_release_hotel_done', 'Hotel cancelled with the supplier.') : t('sp_release_hotel_notes', 'Hotel release completed with notes.'))
        + (notes.length ? '\n- ' + notes.join('\n- ') : ''))
      const res = await tournamentApi.get('/portal/reseller/bookings/' + id)
      setData(res.data)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setReleasing(false) }
  }

  // Group issues by component for inline display + a top-level summary.
  const issuesByComponent = new Map()
  const bookingLevelIssues = []
  for (const iss of integrityIssues) {
    if (iss.Component_ID) {
      const arr = issuesByComponent.get(iss.Component_ID) || []
      arr.push(iss)
      issuesByComponent.set(iss.Component_ID, arr)
    } else {
      bookingLevelIssues.push(iss)
    }
  }

  // Shared extras under a Hotel/Golf card: deferred note, provider failure
  // reason, and any unresolved integrity-check banners for that component.
  function renderCompExtras(comp) {
    if (!comp) return null
    const compIssues = issuesByComponent.get(comp.ID_NO) || []
    const diag = /fail|cancel|pending/.test(comp.Status) ? parseProviderReason(comp) : null
    return (
      <>
        {comp.Status === 'deferred' && comp.BookAfterDate && (
          <div style={{ fontSize: 11.5, color: '#7c3aed', marginTop: 8 }}>
            📅 {t('sp_component_deferred_books', 'Scheduled — books automatically around {date}, when the course opens its booking window.', { date: String(comp.BookAfterDate).slice(0, 10) })}
          </div>
        )}
        {diag && (
          <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
            <strong>{t('sp_provider_reason_label', 'Why this didn’t book:')}</strong> {diag}
          </div>
        )}
        {compIssues.map(iss => (
          <div key={iss.CheckId} style={{ marginTop: 10, padding: '8px 10px', background: '#fff', border: '1px solid #fca5a5', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#991b1b', fontWeight: 600 }}>
              <span>⚠</span>
              <span>
                {iss.DiscrepancyType === 'missing_upstream' && (iss.CheckType === 'hotel'
                  ? t('sp_issue_missing_hotel', 'Provider no longer shows this reservation.')
                  : t('sp_issue_missing_teesheet', 'Course tee sheet does not show this booking.'))}
                {iss.DiscrepancyType === 'status_mismatch' && (
                  t('sp_issue_status_mismatch', 'Provider reports "{upstream}" but our records show "{ours}".', { upstream: iss.UpstreamStatus, ours: iss.OurStatus })
                )}
                {iss.DiscrepancyType === 'preauth_expiring' && (iss.Details || t('sp_issue_preauth_expiring', 'Pre-auth expires soon.'))}
              </span>
            </div>
            {iss.Details && iss.DiscrepancyType !== 'preauth_expiring' && (
              <div style={{ fontSize: 11, color: '#7f1d1d', marginTop: 4, paddingLeft: 24 }}>{iss.Details.replace(/^\[DEMO_SEED\]\s*/, '')}</div>
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, paddingLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{t('sp_issue_checked_at', 'Checked {datetime}', { datetime: new Date(iss.CheckedAt).toLocaleString() })}</span>
              <button onClick={() => resolveIssue(iss.CheckId)}
                style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: 11, fontWeight: 600, background: '#fff', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer' }}>
                {t('sp_btn_mark_resolved', 'Mark resolved')}
              </button>
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <ResellerShell title={t('sp_booking_number_title', 'Booking #{id}', { id: b.ID_NO })}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Link to="/portal/reseller/bookings" style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>{t('sp_back_all_bookings', '← All bookings')}</Link>
        {hasCapturedPayment && !pendingRefund && canIssueRefunds && (
          <button type="button" onClick={() => setShowPartialRefund(true)} disabled={busy}
            style={{ marginLeft: 'auto', padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 6, cursor: 'pointer' }}>
            {t('pr_partial_refund_btn', 'Partial refund')}
          </button>
        )}
        {CANCELLABLE.has(b.Status) && canIssueRefunds && (
          <button type="button" onClick={cancelRefund} disabled={busy}
            style={{ marginLeft: hasCapturedPayment ? 0 : 'auto', padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? t('sp_cancelling', 'Cancelling…') : t('sp_cancel_refund_btn', 'Cancel & refund')}
          </button>
        )}
        {canReleaseHotel && (
          <button type="button" onClick={releaseHotel} disabled={releasing}
            title={t('sp_release_hotel_help', 'This trip is cancelled but the hotel is still confirmed with the supplier — cancel it to release the reservation.')}
            style={{ marginLeft: (CANCELLABLE.has(b.Status) || hasCapturedPayment) ? 0 : 'auto', padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 6, cursor: releasing ? 'default' : 'pointer', opacity: releasing ? 0.6 : 1 }}>
            {releasing ? t('sp_cancelling', 'Cancelling…') : t('sp_release_hotel_btn', 'Cancel hotel with supplier')}
          </button>
        )}
      </div>

      {showPartialRefund && (
        <PartialRefundModal
          kind="booking"
          id={id}
          t={t}
          onClose={() => setShowPartialRefund(false)}
          onDone={reloadDetail}
        />
      )}

      {pendingRefund && pendingRefund.status === 'awaiting_approval' && (
        <div style={{ marginTop: 12 }}>
          <RefundApprovalPanel pending={pendingRefund} canApprove={canApproveRefunds} onDone={reloadDetail} t={t} />
        </div>
      )}
      {pendingRefund && pendingRefund.status !== 'awaiting_approval' && (
        <div style={{ marginTop: 12 }}>
          <RefundInProgressPanel pending={pendingRefund} base={`/portal/reseller/bookings/${id}`} onDone={reloadDetail} t={t} canAct={canIssueRefunds} />
        </div>
      )}

      {/* Top-level integrity banner — shown when this booking has any
          unresolved upstream discrepancy. The component cards below
          repeat the specific issue inline next to the affected line. */}
      {integrityIssues.length > 0 && (
        <div style={{
          marginTop: 12, marginBottom: 4, padding: '12px 14px',
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
          color: '#991b1b',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700 }}>
            <span style={{ fontSize: 16 }}>⚠</span>
            <span>{integrityIssues.length === 1
              ? t('sp_booking_unresolved_issue', 'This booking has {count} unresolved upstream issue.', { count: integrityIssues.length })
              : t('sp_booking_unresolved_issues', 'This booking has {count} unresolved upstream issues.', { count: integrityIssues.length })}</span>
          </div>
          <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 4 }}>
            {t('sp_booking_records_disagree_prefix', 'Our records show this booking as')} <strong>{statusLabel(t, b.Status)}</strong>{t('sp_booking_records_disagree_suffix', ', but the provider disagrees. Review the highlighted lines below and contact the provider to reconcile.')}
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title={t('sp_th_customer', 'Customer')}>
          <Row k={t('sp_name_label', 'Name')} v={b.GolferName} />
          <Row k={t('sp_email', 'Email')} v={b.GolferEmail} />
          <Row k={t('sp_phone', 'Phone')} v={b.GolferPhone || '—'} />
          <Row k={t('sp_golfer_group', 'Group')} v={t('sp_group_golfers_rooms', '{golfers} golfers · {rooms} rooms', { golfers: b.NumGolfers, rooms: b.NumRooms })} />
        </Card>
        <Card title={t('sp_card_trip', 'Trip')}>
          <Row k={t('sp_th_package', 'Package')} v={b.PackageName} />
          <Row k={t('sp_grouping', 'Grouping')} v={b.GroupingName} />
          <Row k={t('dates', 'Dates')} v={`${b.CheckInDate} → ${b.CheckOutDate}`} />
          <Row k={t('sp_total', 'Total')} v={fmtMoney(b.TotalCustomerPaid || 0, b.Currency)} />
          {Number(b.MarginCapDiscount) > 0 && (
            <Row k={t('sp_maxrev_label', 'Maximum revenue per booking')}
              v={<span title={b.MarginCapNote || ''} style={{ color: '#0f766e' }}>{t('sp_maxrev_reduced_line', 'Reduced {amount} to meet the cap', { amount: fmtMoney(Number(b.MarginCapDiscount), b.Currency) })}</span>} />
          )}
          {refundedTotal > 0 && <Row k={t('pr_refunded_line', 'Refunded')} v={<span style={{ color: '#b45309' }}>−{fmtMoney(refundedTotal, b.Currency)}</span>} />}
          {refundedTotal > 0 && <Row k={t('pr_net_after_refunds', 'Net after refunds')} v={<strong>{fmtMoney(Math.max(0, (Number(b.TotalCustomerPaid) || 0) - refundedTotal), b.Currency)}</strong>} />}
          <Row k={t('status', 'Status')} v={<StatusPill status={b.Status} />} />
        </Card>
      </div>

      {/* ─── Hotel ─────────────────────────────────────────────────── */}
      <h3 style={h3}>{t('sp_bk_sec_hotel', 'Hotel')}</h3>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        {hotelDetails.length === 0 ? (
          <div style={{ padding: 14, fontSize: 13, color: '#94a3b8' }}>{t('sp_bk_no_hotel', 'No hotel on this booking.')}</div>
        ) : hotelDetails.map(h => {
          const comp = components.find(c => c.ComponentType === 'hotel' && c.Sequence === h.sequence)
          const hasIssue = comp && (issuesByComponent.get(comp.ID_NO) || []).length > 0
          const hotelRow = hotels.find(hh => hh.Sequence === h.sequence)
          const refundedRooms = hotelRow ? Number(hotelRow.RefundedRooms) || 0 : 0
          return (
            <div key={'h' + h.sequence} style={{ padding: 14, borderBottom: '1px solid #f1f5f9', background: hasIssue ? '#fef2f2' : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{h.name || '—'}</div>
                <ComponentPill status={h.status || (comp && comp.Status)} />
              </div>
              <FieldRow label={t('sp_bk_f_address', 'Hotel Address')} value={h.address} />
              <FieldRow label={t('sp_bk_f_guest_name', 'Guest Name')} value={b.GolferName} />
              <FieldRow label={t('sp_bk_f_total_guests', 'Total Guests')} value={b.NumGolfers != null ? String(b.NumGolfers) : null} />
              <FieldRow label={t('sp_bk_f_checkin', 'Check-In')} value={fmtDateTime(h.checkInDate, h.checkInTime)} />
              <FieldRow label={t('sp_bk_f_checkout', 'Check-Out')} value={fmtDateTime(h.checkOutDate, h.checkOutTime)} />
              <FieldRow label={t('sp_bk_f_hotel_conf', 'Hotel Confirmation')} value={h.confirmation} />
              <FieldRow label={t('sp_bk_f_resort_fees', 'Resort Fees')} value={h.resortFee != null ? fmtMoney(h.resortFee, h.resortFeeCurrency || b.Currency) : null} />
              {refundedRooms > 0 && (
                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 6 }}>↩ {refundedRooms === 1 ? t('pr_room_refunded_one', '{n} room refunded', { n: refundedRooms }) : t('pr_room_refunded_many', '{n} rooms refunded', { n: refundedRooms })}</div>
              )}
              {String(h.source || '').toLowerCase() === 'net_rate'
                && comp && comp.Status === 'awaiting_confirmation'
                && !CANCELLED_TRIP.has(b.Status) && b.Status !== 'cancelled_failed_payment' && (
                <NetRateConfirmForm
                  bookingId={id}
                  sequence={h.sequence}
                  parkedCost={comp.Cost != null ? Number(comp.Cost) : null}
                  currency={b.Currency}
                  canIssueRefunds={canIssueRefunds}
                  onDone={reloadDetail}
                  t={t}
                  fmtMoney={fmtMoney}
                />
              )}
              {renderCompExtras(comp)}
            </div>
          )
        })}
      </div>

      {/* ─── Golf ──────────────────────────────────────────────────── */}
      <h3 style={h3}>{t('sp_bk_sec_golf', 'Golf')}</h3>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        {rounds.length === 0 ? (
          <div style={{ padding: 14, fontSize: 13, color: '#94a3b8' }}>{t('sp_bk_no_golf', 'No rounds on this booking.')}</div>
        ) : rounds.slice().sort((a, b2) => a.RoundIndex - b2.RoundIndex).map(r => {
          const comp = components.find(c => c.ComponentType === 'round' && c.Sequence === r.RoundIndex)
          const status = comp ? comp.Status : r.Status
          const hasIssue = comp && (issuesByComponent.get(comp.ID_NO) || []).length > 0
          const golfers = r.GolfersOnRound != null ? r.GolfersOnRound : b.NumGolfers
          const conf = (comp && comp.ProviderConfirmationNumber) || r.GolfBookingRef || null
          const refundedGolfers = Number(r.RefundedGolfers) || 0
          return (
            <div key={'r' + r.RoundIndex} style={{ padding: 14, borderBottom: '1px solid #f1f5f9', background: hasIssue ? '#fef2f2' : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{r.CourseName || t('sp_itinerary_round_n', 'Round {n}', { n: r.RoundIndex + 1 })}</div>
                <ComponentPill status={status} />
              </div>
              <FieldRow label={t('sp_bk_f_golfer_name', 'Golfer Name')} value={b.GolferName} />
              <FieldRow label={t('sp_bk_f_total_golfers', 'Total Golfers')} value={golfers != null ? String(golfers) : null} />
              <FieldRow label={t('sp_bk_f_date', 'Date')} value={fmtDate(r.PlayDate)} />
              <FieldRow label={t('sp_bk_f_start', 'Start Time')} value={fmtTime(r.TeeTime)} />
              <FieldRow label={t('sp_bk_f_golf_conf', 'Golf Confirmation')} value={conf} />
              <FieldRow label={t('sp_bk_f_cart', 'Cart Included')} value={Number(r.CartCount) > 0 ? t('sp_bk_cart_yes', 'Included') : t('sp_bk_cart_no', 'Not included')} />
              {refundedGolfers > 0 && (
                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 6 }}>↩ {refundedGolfers === 1 ? t('pr_golfer_refunded_one', '{n} golfer refunded', { n: refundedGolfers }) : t('pr_golfer_refunded_many', '{n} golfers refunded', { n: refundedGolfers })}</div>
              )}
              {status === 'awaiting_confirmation' && comp && (
                <RequestConfirmForm bookingId={id} roundIndex={r.RoundIndex} requestedTime={(r.TeeTime || '').replace(/^~/, '')} onDone={reloadDetail} t={t} />
              )}
              {renderCompExtras(comp)}
            </div>
          )
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 style={h3}>{t('sp_heading_payments', 'Payments')}</h3>
        {hasAuthorizedHold && (
          <button type="button" onClick={releaseHolds} disabled={releasing}
            style={{ marginLeft: 'auto', padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 6, cursor: releasing ? 'default' : 'pointer', opacity: releasing ? 0.6 : 1 }}>
            {releasing ? t('sp_releasing', 'Releasing…') : t('sp_release_holds_btn', 'Release authorized holds')}
          </button>
        )}
      </div>
      {hasAuthorizedHold && (
        <div style={{ marginBottom: 8, fontSize: 12, color: '#b45309' }}>
          {t('sp_release_holds_hint', '⚠ A payment is still authorized — the hold is live on the customer’s card. Release it to reverse the pre-authorization.')}
        </div>
      )}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        {payments.length === 0 ? (
          <div style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>{t('sp_no_payments_yet', 'No payments yet.')}</div>
        ) : payments.map(p => (
          <div key={p.ID_NO} style={{ padding: 12, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                {p.PayerName} {p.IsSplitLeader ? '' : t('sp_payment_split_suffix', '(split)')}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                {p.PayerEmail} · {t('sp_payment_authorized_at', 'authorized {datetime}', { datetime: p.AuthorizedAt ? new Date(p.AuthorizedAt).toLocaleString() : '—' })}
                {p.CapturedAt ? ` ${t('sp_payment_captured_at', '· captured {datetime}', { datetime: new Date(p.CapturedAt).toLocaleString() })}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 600 }}>{fmtMoney(p.Amount, p.Currency)}</div>
              <PaymentPill status={p.Status} />
            </div>
          </div>
        ))}
      </div>

      {refundedTotal > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
            <h3 style={{ ...h3, marginTop: 0, marginBottom: 0 }}>{t('pr_refunds_heading', 'Refunds issued')}</h3>
            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#b45309' }}>{fmtMoney(refundedTotal, b.Currency)}</span>
          </div>
          <div style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 8, marginTop: 8 }}>
            {refunds.map((r, i) => (
              <div key={i} style={{ padding: 12, borderBottom: i < refunds.length - 1 ? '1px solid #fef3c7' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#78350f' }}>{r.payerName || t('pr_this_payer', 'this payer')}</div>
                  <div style={{ fontSize: 11, color: '#a16207' }}>
                    {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}{r.reason ? ` · ${r.reason}` : ''}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: '#b45309' }}>−{fmtMoney(r.amount, b.Currency)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <CommunicationHistory bookingId={id} />
    </ResellerShell>
  )
}

function Card({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  )
}
function Row({ k, v }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 13, marginBottom: 6 }}>
      <span style={{ color: '#94a3b8', minWidth: 80 }}>{k}</span>
      <span style={{ flex: 1 }}>{v}</span>
    </div>
  )
}
function ComponentPill({ status }) {
  const { t } = useLanguage()
  const palette = {
    confirmed:               { bg: '#d1fae5', fg: '#065f46' },
    pending:                 { bg: '#fef3c7', fg: '#92400e' },
    deferred:                { bg: '#ede9fe', fg: '#6d28d9' },
    awaiting_confirmation:    { bg: '#f5f3ff', fg: '#6d28d9' },
    failed_pending_action:   { bg: '#fee2e2', fg: '#991b1b' },
    cancelled:               { bg: '#e2e8f0', fg: '#475569' },
    refunded:                { bg: '#e2e8f0', fg: '#475569' },
  }[status] || { bg: '#e2e8f0', fg: '#475569' }
  return <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: palette.bg, color: palette.fg }}>{statusLabel(t, status)}</span>
}
function PaymentPill({ status }) {
  const { t } = useLanguage()
  const palette = {
    authorized: { bg: '#dbeafe', fg: '#1e40af' },
    captured:   { bg: '#d1fae5', fg: '#065f46' },
    voided:     { bg: '#e2e8f0', fg: '#475569' },
    refunded:   { bg: '#fef3c7', fg: '#92400e' },
    declined:   { bg: '#fee2e2', fg: '#991b1b' },
    pending:    { bg: '#e2e8f0', fg: '#475569' },
  }[status] || { bg: '#e2e8f0', fg: '#475569' }
  return <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: palette.bg, color: palette.fg }}>{statusLabel(t, status)}</span>
}

const th = { textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }
const td = { padding: '12px 14px', verticalAlign: 'top' }
const h3 = { fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginTop: 24, marginBottom: 8 }
const errorBox = { padding: 10, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }

// Pull the human-readable provider failure reason out of a component's stored
// response (why a hotel/round didn't book — e.g. "single players can't book an
// empty tee time"). We surface ONLY this reason/error string, never the full
// raw provider/gateway payload.
function parseProviderReason(c) {
  let raw = c?.ProviderResponseRaw
  if (raw && typeof raw === 'string') { try { raw = JSON.parse(raw) } catch { return null } }
  if (!raw || typeof raw !== 'object') return null
  const text = raw.reason || raw.error || raw.message || null
  return text ? String(text) : null
}

// Format a date-ish value (date or datetime) as a local calendar date. Slices
// to the date part first so a datetime doesn't shift the day across timezones.
function fmtDate(v) {
  if (!v) return '—'
  const d = new Date(String(v).slice(0, 10) + 'T00:00:00')
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function fmtMoney(value, currency) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const formatted = '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${currency} ${formatted}` : formatted
}

// "15:00" / "09:20:00" -> "3:00 PM" / "9:20 AM". '' for empty/invalid.
function fmtTime(v) {
  if (!v) return ''
  const s = String(v).trim()
  // Already 12-hour with an AM/PM suffix (Expedia content, e.g. "4:00 PM") —
  // pass through normalized so it isn't mis-read as AM. Only bare 24-hour
  // "HH:MM" (RateHawk/tee-times) gets converted.
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/)
  if (ampm) return `${Number(ampm[1])}:${ampm[2]} ${ampm[3].toUpperCase()}`
  const m = s.match(/^(\d{1,2}):(\d{2})/)
  if (!m) return String(v)
  let h = Number(m[1]); const min = m[2]
  const ap = h >= 12 ? 'PM' : 'AM'
  h = h % 12; if (h === 0) h = 12
  return `${h}:${min} ${ap}`
}
// Date + optional time: "Oct 27, 2026 · 3:00 PM" (date alone if no time).
function fmtDateTime(date, time) {
  const d = fmtDate(date)
  const tt = fmtTime(time)
  return tt && d !== '—' ? `${d} · ${tt}` : d
}

// One label/value line in a Hotel/Golf detail card.
function FieldRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '3px 0' }}>
      <div style={{ fontSize: 12.5, color: '#94a3b8', width: 160, flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#334155' }}>{value == null || value === '' ? '—' : value}</div>
    </div>
  )
}
